import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Check, X, Users, ShoppingBag, Brain, MessageCircle, Hash, Bot, Ticket, Phone, Video, KeyRound, RefreshCw, ExternalLink } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDateLong } from '@/lib/format';
import { useTranslation } from 'react-i18next';

interface LicenseInfo {
  valid: boolean;
  plan: string;
  plan_version?: number;
  max_agents: number;
  max_shops: number;
  addons: { ai_full: boolean; internal_comm: boolean; whatsapp: boolean; chatbot?: boolean };
  monthly_price: number;
  monthly_price_cents?: number | null;
  currency?: string;
  expires_at: string | null;
  is_active: boolean;
  expired?: boolean;
  self_hosted?: boolean;
  provision_status?: string;
  created_at?: string;
}

interface UsageMetric { used: number; limit: number | null; }
interface AiUsage extends UsageMetric { enabled: boolean; period_start: string | null; period_end: string | null; }
interface Usage { agents: UsageMetric; shops: UsageMetric; ai: AiUsage; chatbot?: AiUsage; }

interface Quota { used: number; included: number | null; over: number; unit_cents: number | null; amount_cents: number; }
interface Onboarding { email: string; temp_password: string; grace_until: string | null; portal_url: string; }
interface V2 {
  in_grace?: boolean; grace_until?: string | null;
  plan: string; plan_version: number; plan_name: string; currency: string | null; limit_mode: 'overage' | 'block';
  period: { start: string; end: string };
  counters: Record<string, number>;
  quotas: Record<string, Quota>;
  projected_overage_cents: number;
  projected_overage_lines: { kind: string; over: number; unit_cents: number; amount_cents: number }[];
  identity: { slug: string | null; source: 'env' | 'db' | 'none'; has_report_key: boolean };
  last_report: { ok?: boolean; at?: string; error?: string; skipped?: string } | null;
}

const PLAN_LABELS: Record<string, string> = {
  starter: 'Starter', growth: 'Growth', suite: 'Suite', pro: 'Professional', enterprise: 'Enterprise', beta: 'Beta', unlimited: 'Unlimited',
};

const money = (cents: number, currency: string | null) => {
  const sym = currency === 'eur' ? '€' : '$';
  return `${sym}${(cents / 100).toFixed(2)}`;
};

function StatTile({ icon: Icon, label, used, limit, enabled = true, disabledHint, overAmount, currency }: {
  icon: any; label: string; used: number; limit: number | null; enabled?: boolean; disabledHint?: string; overAmount?: number; currency?: string | null;
}) {
  const unlimited = limit === null;
  const pct = unlimited ? 0 : (limit! > 0 ? Math.min(100, Math.round((used / limit!) * 100)) : 100);
  const atLimit = !unlimited && used >= limit!;
  const near = !atLimit && pct >= 80;
  const barColor = atLimit ? 'bg-red-500' : near ? 'bg-amber-500' : 'bg-[#0F99FD]';
  const numColor = atLimit ? 'text-red-600' : near ? 'text-amber-600' : 'text-slate-900';
  return (
    <div className="rounded-lg border border-slate-100 bg-white/60 p-2.5">
      <div className="mb-1.5 flex items-center gap-1.5 text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] font-semibold uppercase tracking-wider truncate">{label}</span>
      </div>
      {!enabled ? (
        <div className="text-[11px] font-medium text-slate-400">{disabledHint || 'Deaktiviert'}</div>
      ) : unlimited ? (
        <div className="text-sm font-bold text-slate-900">{used} <span className="text-[11px] font-medium text-slate-400">· ∞</span></div>
      ) : (
        <>
          <div className={`text-sm font-bold ${numColor}`}>{used}<span className="text-[11px] font-medium text-slate-400"> / {limit}</span></div>
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
          {atLimit && overAmount ? (
            <div className="mt-1 text-[10px] font-medium text-red-600">+{used - limit!} über Paket · {money(overAmount, currency || 'usd')}</div>
          ) : atLimit ? (
            <div className="mt-1 text-[10px] font-medium text-red-600">Limit erreicht</div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function LicenseSection() {
  const { t } = useTranslation();
  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [v2, setV2] = useState<V2 | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [agentKey, setAgentKey] = useState<string | null | undefined>(undefined);
  const [agentBusy, setAgentBusy] = useState(false);
  // Lizenz-Verknüpfung (slug + Schlüssel) — nur wenn nicht per Provisioning gesetzt
  const [linkSlug, setLinkSlug] = useState('');
  const [linkKey, setLinkKey] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [countdown, setCountdown] = useState('');

  const load = () => {
    setIsLoading(true);
    api.get('/license/usage')
      .then(res => {
        const d = res.data?.data;
        if (d) {
          setLicense(d.license || null); setUsage(d.usage || null); setV2(d.v2 || null);
          if (typeof d.ai_enabled === 'boolean') setAiEnabled(d.ai_enabled);
          if (d.v2?.identity?.slug) setLinkSlug(d.v2.identity.slug);
          if (d.v2?.in_grace) {
            api.get('/license/onboarding').then(r => setOnboarding(r.data?.data || null)).catch(() => setOnboarding(null));
          } else setOnboarding(null);
        }
      })
      .catch(() => {
        api.get('/license').then(res => setLicense(res.data?.data || null)).catch(() => setLicense(null));
      })
      .finally(() => setIsLoading(false));
  };

  const genAgentKey = async () => {
    setAgentBusy(true);
    try {
      const r = await api.post('/license/agent-key');
      setAgentKey(r.data?.data?.agent_api_key || null);
      toast.success(t('common:x2.agentKeyCreated', 'Agent-Key erstellt'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Fehlgeschlagen');
    } finally { setAgentBusy(false); }
  };

  const toggleAi = async (next: boolean) => {
    setAiBusy(true);
    setAiEnabled(next);
    try {
      await api.put('/license/ai-toggle', { enabled: next });
      toast.success(next ? 'KI aktiviert' : 'KI deaktiviert');
    } catch (e: any) {
      setAiEnabled(!next);
      toast.error(e?.response?.data?.message || 'Änderung fehlgeschlagen');
    } finally { setAiBusy(false); }
  };

  const saveLink = async () => {
    setLinkBusy(true);
    try {
      const r = await api.put('/license/settings', { slug: linkSlug.trim(), report_key: linkKey.trim() });
      const ok = r.data?.data?.license_valid;
      toast.success(ok ? 'Lizenz verknüpft' : 'Gespeichert — Lizenz noch nicht aktiv');
      setLinkKey('');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally { setLinkBusy(false); }
  };

  const reportNow = async () => {
    setReportBusy(true);
    try {
      const r = await api.post('/license/report-now');
      const d = r.data?.data;
      if (d?.ok) toast.success('Nutzung gemeldet'); else toast.error(d?.error || d?.skipped || 'Meldung fehlgeschlagen');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Meldung fehlgeschlagen');
    } finally { setReportBusy(false); }
  };

  // Übergangsphase geri sayımı
  useEffect(() => {
    const until = v2?.grace_until ? new Date(v2.grace_until).getTime() : 0;
    if (!until) { setCountdown(''); return; }
    const tick = () => {
      const ms = until - Date.now();
      if (ms <= 0) { setCountdown('abgelaufen'); return; }
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
      setCountdown(`${h} Std. ${m} Min.`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [v2?.grace_until]);

  useEffect(() => {
    load();
    api.get('/license/agent-key')
      .then(res => setAgentKey(res.data?.data?.agent_api_key ?? null))
      .catch(() => setAgentKey(undefined));
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!license) {
    return (
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardContent className="py-12 text-center">
          <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{t('common:x2.noLicenseInfo', 'Keine Lizenzinformationen verfügbar.')}</p>
        </CardContent>
      </Card>
    );
  }

  const isV2 = (v2?.plan_version ?? 0) === 2;
  const cur = v2?.currency || license.currency || (isV2 ? 'usd' : 'eur');
  const planLabel = v2?.in_grace ? 'Übergangsphase' : (v2?.plan_name || PLAN_LABELS[license.plan] || license.plan);
  const expiresDate = license.expires_at ? new Date(license.expires_at) : null;
  const expiresFormatted = expiresDate ? formatDateLong(expiresDate) : '–';
  const isExpiringSoon = expiresDate && (expiresDate.getTime() - Date.now()) < 30 * 24 * 60 * 60 * 1000;
  const periodEnd = v2?.period?.end ? formatDateLong(new Date(v2.period.end)) : (usage?.ai?.period_end ? formatDateLong(new Date(usage.ai.period_end)) : null);
  const priceLabel = license.self_hosted || license.monthly_price === 0
    ? 'Kostenlos'
    : license.monthly_price_cents != null ? `${money(license.monthly_price_cents, cur)} / Monat` : `€${license.monthly_price} / Monat`;

  const q = v2?.quotas || {};
  const needsLink = v2 && (!v2.identity.slug || !v2.identity.has_report_key) && !license.self_hosted;
  const showLinkCard = v2 && v2.identity.source !== 'env' && !license.self_hosted
    ? true
    : Boolean(v2 && !v2.identity.has_report_key && !license.self_hosted);

  const addons = [
    { label: 'AI Agent', icon: Brain, active: license.addons?.ai_full },
    { label: 'Interne Komm.', icon: MessageCircle, active: license.addons?.internal_comm },
    { label: 'WhatsApp', icon: Hash, active: license.addons?.whatsapp },
    { label: 'Chatbot', icon: Bot, active: license.addons?.chatbot },
  ];

  return (
    <div className="space-y-3">
      {/* Header: Plan · Preis · Status · KI-Toggle */}
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardContent className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F99FD]/10 text-[#0F99FD]">
              <Shield className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900">{planLabel}</span>
                <Badge className={`rounded-full px-2 py-0 text-[10px] font-bold ${
                  license.valid ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {license.valid ? 'Aktiv' : license.expired ? 'Abgelaufen' : 'Inaktiv'}
                </Badge>
                {isV2 && (
                  <Badge className={`rounded-full px-2 py-0 text-[10px] font-semibold ${v2!.limit_mode === 'block' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {v2!.limit_mode === 'block' ? 'Kostenkontrolle: Stopp am Limit' : 'Über Paket: nutzungsbasiert'}
                  </Badge>
                )}
              </div>
              <div className="text-[11px] text-slate-500">
                {priceLabel}
                {license.self_hosted && ' · Self-Hosted'}
                {!license.self_hosted && periodEnd && <> · Abrechnungszeitraum bis <span className="font-medium">{periodEnd}</span></>}
                {!license.self_hosted && !periodEnd && expiresDate && (
                  <> · gültig bis <span className={isExpiringSoon ? 'text-amber-600 font-medium' : ''}>{expiresFormatted}</span></>
                )}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <div className="text-right leading-tight">
              <div className="text-xs font-semibold text-slate-900">{t('common:x2.aiAssistant', 'KI-Assistent')}</div>
              <div className="text-[10px] text-slate-400">{aiEnabled ? 'Aktiv' : 'Deaktiviert'}</div>
            </div>
            <Switch checked={aiEnabled} onCheckedChange={toggleAi} disabled={aiBusy} />
          </label>
        </CardContent>
      </Card>

      {/* Übergangsphase: mevcut müşteri 24 saat içinde paket seçer; panel giriş bilgileri burada */}
      {v2?.in_grace && (
        <Card className="border border-[#0F99FD]/40 bg-[#0F99FD]/5 backdrop-blur-xl ring-1 ring-[#0F99FD]/20">
          <CardContent className="px-4 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#0b7ad1]">Neues Lizenzsystem · Übergangsphase</div>
                <div className="mt-1 text-sm font-bold text-slate-900">
                  Bitte wählen Sie innerhalb von 24 Stunden Ihr Paket{countdown && <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-[#0b7ad1] ring-1 ring-[#0F99FD]/30">noch {countdown}</span>}
                </div>
                <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-600">
                  VynDesk stellt auf Pakete um (Starter, Growth, Suite). Bis dahin läuft Ihr Desk ohne Limits weiter. Melden Sie sich im Lizenz-Portal an, wählen Sie Ihr Paket und hinterlegen Sie die Zahlung — Ihr Desk, Ihre Daten und Ihre Adresse bleiben unverändert.
                  {v2.grace_until && <> Frist: <span className="font-semibold text-slate-900">{formatDateLong(new Date(v2.grace_until))}</span>.</>}
                </p>
              </div>
              <a href={onboarding?.portal_url || 'https://app.vyndesk.com/license'} target="_blank" rel="noreferrer"
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#101319] px-3 text-xs font-semibold text-white hover:bg-black">
                Zum Lizenz-Portal <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
            {onboarding && (
              <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <div className="rounded-md border border-[#0F99FD]/30 bg-white px-3 py-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">E-Mail</div>
                  <div className="truncate text-sm font-semibold text-slate-900">{onboarding.email}</div>
                </div>
                <div className="rounded-md border border-[#0F99FD]/30 bg-white px-3 py-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Passwort (temporär)</div>
                  <div className="font-mono text-sm font-semibold text-slate-900">{onboarding.temp_password}</div>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(`${onboarding.email}\n${onboarding.temp_password}`); toast.success('Kopiert'); }}
                  className="inline-flex h-full min-h-[44px] items-center justify-center rounded-md border border-[#0F99FD]/30 bg-white px-3 text-xs font-semibold text-[#0b7ad1] hover:bg-[#0F99FD]/10">Kopieren</button>
                <p className="sm:col-span-3 text-[11px] text-slate-500">Anmelden unter app.vyndesk.com mit diesen Daten, Paket wählen, Zahlung abschließen. Das Passwort bitte nach dem Login ändern. Diese Zugangsdaten verschwinden, sobald das Paket aktiv ist.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Verbrauch — v2: 6 Kacheln (Paketsystem) */}
      {isV2 && v2 ? (
        <>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-6">
            <StatTile icon={Ticket} label="Anfragen" used={q.tickets?.used ?? 0} limit={q.tickets?.included ?? null} overAmount={q.tickets?.amount_cents} currency={cur} />
            <StatTile icon={Brain} label="AI-Interaktionen" used={q.ai_interactions?.used ?? 0} limit={q.ai_interactions?.included ?? null} overAmount={q.ai_interactions?.amount_cents} currency={cur} />
            <StatTile icon={Users} label="Seats" used={q.seats?.used ?? 0} limit={q.seats?.included ?? null} />
            <StatTile icon={Phone} label="Telefon Human" used={q.phone_human_minutes?.used ?? 0} limit={q.phone_human_minutes?.included ?? null} overAmount={q.phone_human_minutes?.amount_cents} currency={cur} />
            <StatTile icon={Phone} label="Telefon AI" used={q.phone_ai_minutes?.used ?? 0} limit={q.phone_ai_minutes?.included ?? null} overAmount={q.phone_ai_minutes?.amount_cents} currency={cur} />
            <StatTile icon={Video} label="Meeting-Min." used={q.meeting_participant_minutes?.used ?? 0} limit={q.meeting_participant_minutes?.included ?? null} overAmount={q.meeting_participant_minutes?.amount_cents} currency={cur} />
          </div>
          {v2.projected_overage_cents > 0 && (
            <Card className="border border-amber-200/70 bg-amber-50/60 backdrop-blur-xl ring-1 ring-black/5">
              <CardContent className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                <span className="text-xs font-semibold text-amber-800">Voraussichtliche Nutzung über Paket in diesem Zeitraum</span>
                <span className="text-sm font-bold text-amber-900">{money(v2.projected_overage_cents, cur)}</span>
              </CardContent>
            </Card>
          )}
        </>
      ) : usage && (
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <StatTile icon={Users} label="Agents" used={usage.agents.used} limit={usage.agents.limit} />
          <StatTile icon={ShoppingBag} label="Shops" used={usage.shops.used} limit={usage.shops.limit} />
          <StatTile icon={Brain} label="KI-Tickets" used={usage.ai.used} limit={usage.ai.limit} enabled={usage.ai.enabled} disabledHint="Kein AI-Paket" />
          <StatTile icon={Bot} label="Chatbot" used={usage.chatbot?.used ?? 0} limit={usage.chatbot?.limit ?? null} enabled={!!usage.chatbot?.enabled} disabledHint="Kein Chatbot-Paket" />
        </div>
      )}

      {/* Enthalten / Add-ons */}
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardContent className="px-4 py-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{isV2 ? 'Enthalten' : 'Add-ons'}</span>
            <a href="https://app.vyndesk.com/license" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0F99FD] hover:underline">
              Paket verwalten <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {addons.map(a => (
              <div key={a.label} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                a.active ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-100 bg-slate-50/50'
              }`}>
                <a.icon className={`h-3.5 w-3.5 shrink-0 ${a.active ? 'text-emerald-600' : 'text-slate-400'}`} />
                <span className={`text-xs font-medium truncate ${a.active ? 'text-slate-900' : 'text-slate-400'}`}>{a.label}</span>
                {a.active
                  ? <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  : <X className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-300" />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lizenz-Verknüpfung (slug + Schlüssel) + Nutzungsmeldung */}
      {v2 && !license.self_hosted && (
        <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
          <CardContent className="px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Lizenz-Verknüpfung</span>
              <span className="text-[10px] text-slate-400">
                {v2.last_report?.ok ? `Zuletzt gemeldet ${v2.last_report.at ? formatDateLong(new Date(v2.last_report.at)) : ''}`
                  : v2.last_report?.error ? `Meldung fehlgeschlagen: ${v2.last_report.error}` : 'Noch keine Meldung'}
              </span>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[160px] flex-1">
                <div className="mb-1 text-[10px] text-slate-500">Lizenz-Slug</div>
                <input value={linkSlug} onChange={e => setLinkSlug(e.target.value)} disabled={v2.identity.source === 'env'}
                  placeholder="z. B. meinefirma"
                  className="h-8 w-full rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none focus:border-[#0F99FD] disabled:bg-slate-50 disabled:text-slate-400" />
              </div>
              <div className="min-w-[220px] flex-[2]">
                <div className="mb-1 text-[10px] text-slate-500">Lizenzschlüssel {v2.identity.has_report_key && <span className="text-emerald-600">· hinterlegt</span>}</div>
                <input value={linkKey} onChange={e => setLinkKey(e.target.value)} placeholder={v2.identity.has_report_key ? 'Neuen Schlüssel eintragen (optional)' : 'vdl_…  (aus app.vyndesk.com → Lizenzen)'}
                  className="h-8 w-full rounded-md border border-slate-200 bg-white px-2.5 font-mono text-xs text-slate-700 outline-none focus:border-[#0F99FD]" />
              </div>
              <button onClick={saveLink} disabled={linkBusy || (!linkSlug.trim() && !linkKey.trim())}
                className="h-8 rounded-md bg-[#0F99FD] px-3 text-xs font-semibold text-white hover:bg-[#0d8ae0] disabled:opacity-50">Speichern</button>
              <button onClick={reportNow} disabled={reportBusy || !v2.identity.has_report_key}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 px-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                <RefreshCw className={`h-3.5 w-3.5 ${reportBusy ? 'animate-spin' : ''}`} /> Jetzt melden
              </button>
            </div>
            {needsLink && (
              <p className="mt-1.5 text-[10px] text-amber-600">Ohne Slug und Schlüssel wird die Nutzung nicht gemeldet; Limits gelten dann nicht.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Agent Cockpit — Desktop-App API Key (nur Admin) */}
      {agentKey !== undefined && (
        <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
          <CardContent className="px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t('common:x2.agentCockpitDesktop', 'Agent Cockpit (Desktop-App)')}</span>
            </div>
            <div className="flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <code className="flex-1 truncate rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700 border border-slate-100">
                {agentKey || 'Noch kein Key erstellt'}
              </code>
              {agentKey && (
                <button
                  onClick={() => { navigator.clipboard.writeText(agentKey); toast.success(t('common:x1.copied', 'Kopiert')); }}
                  className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >{t('common:x2.copy', 'Kopieren')}</button>
              )}
              <button
                onClick={genAgentKey}
                disabled={agentBusy}
                className="rounded-md bg-[#0F99FD] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#0d8ae0] disabled:opacity-50"
              >{agentKey ? 'Neu' : 'Erstellen'}</button>
            </div>
            <p className="mt-1.5 text-[10px] text-slate-400">Diesen Key in der Vyndesk-Agent-Desktop-App eintragen, um das Desk fernzusteuern.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
